/* eslint-disable @eslint-react/dom-no-unknown-property */
import { useState, useMemo, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import ReactMapbox, { Source, Layer, MapRef } from 'react-map-gl';
import type { FeatureCollection, Feature, Polygon } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import { OwnershipNode } from './types';
import { formatDepthBand } from './data-helpers';
import styles from './severance-visualization.module.scss';

const MAPBOX_TOKEN = (window as any).CONFIG?.mapbox?.token as
  string | undefined;
const MAPBOX_STYLE =
  'mapbox://styles/carbondesignsystem/ck7c8ce1y05h61ipb2fixfe76';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_DEPTH = 10_000;
const SLAB_GAP = 50;
const FT_PER_DEG_LAT = 364_000;

// Colors for the two output zones
const ZONE_COLORS = [
  { fill: 'rgba(15, 98, 254, 0.3)', stroke: '#0f62fe' },
  { fill: 'rgba(36, 161, 72, 0.3)', stroke: '#24a148' },
];

const ZONE_3D_COLORS = ['#0f62fe', '#24a148'];
const BEFORE_COLOR = '#8d8d8d';

// Local geography shape used by severance components.
export interface SeveranceGeography {
  id: number;
  referenceLocationName: string;
  searchText?: string;
  geometryJson: GeoJSON.Geometry | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// * Resolve nullable depth_range[1] to a usable number (null = Infinity).
// @param raw The raw depth value, or null.
// @returns The depth as a number, with null coerced to Infinity.
function resolveDepthTo(raw: number | null): number {
  return raw ?? Infinity;
}

function findGeography(
  geometryName: string,
  geographies: SeveranceGeography[]
): SeveranceGeography | undefined {
  return geographies.find(
    (g) =>
      (g.referenceLocationName === geometryName ||
        g.searchText === geometryName) &&
      g.geometryJson
  );
}

// ─── 3D Utility functions (from ownership-map-modal) ─────────────────────────

function computeCenter(
  geographies: SeveranceGeography[]
): { lng: number; lat: number } | null {
  let sumLng = 0,
    sumLat = 0,
    count = 0;
  for (const g of geographies) {
    const geo = g.geometryJson;
    if (!geo || (geo.type !== 'Polygon' && geo.type !== 'MultiPolygon'))
      continue;
    const rings: number[][][] =
      geo.type === 'MultiPolygon'
        ? (geo as GeoJSON.MultiPolygon).coordinates.flat()
        : (geo as GeoJSON.Polygon).coordinates;
    for (const ring of rings) {
      for (const [lng, lat] of ring) {
        sumLng += lng;
        sumLat += lat;
        count++;
      }
    }
  }
  if (count === 0) return null;
  return { lng: sumLng / count, lat: sumLat / count };
}

function lngLatToFt(
  lng: number,
  lat: number,
  centerLng: number,
  centerLat: number
): [number, number] {
  const ftPerDegLng = FT_PER_DEG_LAT * Math.cos((centerLat * Math.PI) / 180);
  return [(lng - centerLng) * ftPerDegLng, (lat - centerLat) * FT_PER_DEG_LAT];
}

function buildShape(
  coordinates: number[][][],
  centerLng: number,
  centerLat: number
): THREE.Shape {
  const shape = new THREE.Shape();
  const outer = coordinates[0];
  outer.forEach(([lng, lat], i) => {
    const [x, y] = lngLatToFt(lng, lat, centerLng, centerLat);
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  });
  for (let r = 1; r < coordinates.length; r++) {
    const hole = new THREE.Path();
    coordinates[r].forEach(([lng, lat], i) => {
      const [x, y] = lngLatToFt(lng, lat, centerLng, centerLat);
      if (i === 0) hole.moveTo(x, y);
      else hole.lineTo(x, y);
    });
    shape.holes.push(hole);
  }
  return shape;
}

function polygonCentroidFt(
  coordinates: number[][][],
  centerLng: number,
  centerLat: number
): [number, number] {
  const ring = coordinates[0];
  const pts = ring.map(([lng, lat]) =>
    lngLatToFt(lng, lat, centerLng, centerLat)
  );
  return [
    pts.reduce((s, [x]) => s + x, 0) / pts.length,
    pts.reduce((s, [, y]) => s + y, 0) / pts.length,
  ];
}

// ─── 3D Sub-components ───────────────────────────────────────────────────────

function SceneLighting() {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10000, 8000, 12000]} intensity={0.9} />
      <directionalLight position={[-8000, -6000, 5000]} intensity={0.3} />
    </>
  );
}

function SeveranceSlab({
  shape,
  color,
  thickness,
  posZ,
  opacity,
  label,
  centroidFt,
  depthWrite = true,
}: {
  shape: THREE.Shape;
  color: string;
  thickness: number;
  posZ: number;
  opacity: number;
  label: string;
  centroidFt: [number, number];
  depthWrite?: boolean;
}) {
  const geometry = useMemo(
    () =>
      new THREE.ExtrudeGeometry(shape, {
        depth: thickness,
        bevelEnabled: false,
      }),
    [shape, thickness]
  );
  const edgeGeometry = useMemo(
    () => new THREE.EdgesGeometry(geometry),
    [geometry]
  );

  return (
    <group position={[0, 0, posZ]}>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color={color}
          transparent
          opacity={opacity}
          side={THREE.DoubleSide}
          depthWrite={depthWrite}
        />
      </mesh>
      <lineSegments geometry={edgeGeometry}>
        <lineBasicMaterial color={color} transparent opacity={0.6} />
      </lineSegments>
      <Html
        position={[centroidFt[0], centroidFt[1], thickness / 2]}
        center
        style={{ pointerEvents: 'none' }}
      >
        <div className={styles.slab3dLabel}>{label}</div>
      </Html>
    </group>
  );
}

// ─── Depth Severance 3D ──────────────────────────────────────────────────────

interface DepthSeverance3DProps {
  sourceNode: OwnershipNode;
  outputNodes: OwnershipNode[];
  geographies: SeveranceGeography[];
  showAfter: boolean;
}

function DepthSeverance3D({
  sourceNode,
  outputNodes,
  geographies,
  showAfter,
}: DepthSeverance3DProps) {
  const sorted = useMemo(
    () =>
      [...outputNodes].sort(
        (a, b) =>
          a.property.volume.depthRange[0] - b.property.volume.depthRange[0]
      ),
    [outputNodes]
  );

  // Find the geography for the source tract
  const geo = findGeography(
    sourceNode.property.volume.geometryName ?? '',
    geographies
  );

  // Build 3D shape from polygon
  const sceneData = useMemo(() => {
    const geoJson = geo?.geometryJson;
    if (!geoJson || geoJson.type !== 'Polygon') return null;
    const polyCoords = (geoJson as GeoJSON.Polygon).coordinates;

    const center = computeCenter([geo]);
    if (!center) return null;

    const shape = buildShape(polyCoords, center.lng, center.lat);
    const centroid = polygonCentroidFt(polyCoords, center.lng, center.lat);

    // Before slab: full source depth range
    const sourceRange = sourceNode.property.volume.depthRange;
    const beforeFrom = sourceRange[0];
    const beforeTo =
      resolveDepthTo(sourceRange[1]) === Infinity
        ? MAX_DEPTH
        : resolveDepthTo(sourceRange[1]);

    // After slabs: one per output node
    const afterSlabs = sorted.map((node, i) => {
      const [rawFrom, rawToOrNull] = node.property.volume.depthRange;
      const rawTo = resolveDepthTo(rawToOrNull);
      const depthFrom = rawFrom;
      const depthTo = rawTo === Infinity ? MAX_DEPTH : rawTo;
      return {
        depthFrom,
        depthTo,
        color: ZONE_3D_COLORS[i % ZONE_3D_COLORS.length],
        label: formatDepthBand([rawFrom, rawToOrNull]),
      };
    });

    // Compute bounding box of shape footprint
    const pts = shape.getPoints();
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    for (const p of pts) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const extentXY = Math.max(maxX - minX, maxY - minY);
    const extentZ = beforeTo - beforeFrom;

    return {
      shape,
      centroid,
      beforeFrom,
      beforeTo,
      afterSlabs,
      extentXY,
      extentZ,
    };
  }, [geo, sourceNode, sorted]);

  if (!sceneData) {
    return (
      <div className={styles.mapFallback}>
        No polygon data available for {sourceNode.property.volume.geometryName}.
      </div>
    );
  }

  const {
    shape,
    centroid,
    beforeFrom,
    beforeTo,
    afterSlabs,
    extentXY,
    extentZ,
  } = sceneData;

  // Orbit target = centroid of shape at mid-depth
  const midZ = -(beforeFrom + beforeTo) / 2;
  const target: [number, number, number] = [centroid[0], centroid[1], midZ];
  // Camera distance based on the diagonal of the 3D bounding box
  const camDistance = Math.sqrt(extentXY * extentXY + extentZ * extentZ) * 1.5;

  return (
    <div className={styles.depth3dContainer}>
      <div className={styles.depth3dCanvas}>
        <Canvas
          camera={{
            position: [
              centroid[0] + camDistance * 0.8,
              centroid[1] - camDistance * 0.5,
              midZ + camDistance * 0.3,
            ],
            fov: 45,
            near: 1,
            far: 100000,
            up: [0, 0, 1],
          }}
          style={{ background: '#f4f4f4' }}
        >
          <SceneLighting />
          <OrbitControls
            target={target}
            enablePan
            enableZoom
            enableRotate
            minDistance={500}
            maxDistance={30000}
          />

          {!showAfter && (
            <SeveranceSlab
              shape={shape}
              color={BEFORE_COLOR}
              thickness={Math.max(beforeTo - beforeFrom - SLAB_GAP, 10)}
              posZ={-beforeTo + SLAB_GAP / 2}
              opacity={0.4}
              label={formatDepthBand(sourceNode.property.volume.depthRange)}
              centroidFt={centroid}
            />
          )}

          {showAfter &&
            afterSlabs.map((slab, i) => (
              <SeveranceSlab
                key={i}
                shape={shape}
                color={slab.color}
                thickness={Math.max(
                  slab.depthTo - slab.depthFrom - SLAB_GAP,
                  10
                )}
                posZ={-slab.depthTo + SLAB_GAP / 2}
                opacity={0.4}
                label={slab.label}
                centroidFt={centroid}
              />
            ))}
        </Canvas>
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        {!showAfter ? (
          <div className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{
                backgroundColor: BEFORE_COLOR,
                borderColor: BEFORE_COLOR,
              }}
            />
            {sourceNode.property.volume.geometryName} &middot;{' '}
            {formatDepthBand(sourceNode.property.volume.depthRange)}
          </div>
        ) : (
          sorted.map((node, i) => {
            const color = ZONE_3D_COLORS[i % ZONE_3D_COLORS.length];
            return (
              <div key={node.id} className={styles.legendItem}>
                <span
                  className={styles.legendSwatch}
                  style={{
                    backgroundColor: color,
                    borderColor: color,
                    opacity: 0.5,
                  }}
                />
                {formatDepthBand(node.property.volume.depthRange)}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Geospatial Severance Map ────────────────────────────────────────────────

interface GeospatialSeveranceProps {
  sourceNode: OwnershipNode;
  outputNodes: OwnershipNode[];
  geographies: SeveranceGeography[];
  showAfter: boolean;
}

function GeospatialSeveranceMap({
  sourceNode,
  outputNodes,
  geographies,
  showAfter,
}: GeospatialSeveranceProps) {
  const mapRef = useRef<MapRef>(null);

  // Build GeoJSON features
  const { beforeFeature, afterFeatures, bounds } = useMemo(() => {
    const sourceGeo = findGeography(
      sourceNode.property.volume.geometryName ?? '',
      geographies
    );
    if (!sourceGeo || !sourceGeo.geometryJson)
      return { beforeFeature: null, afterFeatures: null, bounds: null };

    const before: Feature<Polygon> = {
      type: 'Feature',
      properties: { name: sourceNode.property.volume.geometryName },
      geometry: sourceGeo.geometryJson as GeoJSON.Polygon,
    };

    const after: Feature<Polygon>[] = outputNodes
      .map((node, i) => {
        const geo = findGeography(
          node.property.volume.geometryName ?? '',
          geographies
        );
        if (!geo || !geo.geometryJson) return null;
        return {
          type: 'Feature' as const,
          properties: {
            name: node.property.volume.geometryName,
            colorIndex: i,
          },
          geometry: geo.geometryJson as GeoJSON.Polygon,
        };
      })
      .filter(Boolean) as Feature<Polygon>[];

    // Compute bounds from all coordinates
    const allCoords: number[][] = [];
    const extractCoords = (geom: GeoJSON.Geometry) => {
      if (geom.type === 'Polygon') {
        for (const ring of (geom as GeoJSON.Polygon).coordinates) {
          for (const coord of ring) allCoords.push(coord);
        }
      }
    };
    extractCoords(sourceGeo.geometryJson);
    for (const f of after) extractCoords(f.geometry);

    if (allCoords.length === 0)
      return { beforeFeature: null, afterFeatures: null, bounds: null };

    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    for (const [lng, lat] of allCoords) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }

    return {
      beforeFeature: before,
      afterFeatures: after,
      bounds: [
        [minLng, minLat],
        [maxLng, maxLat],
      ] as [[number, number], [number, number]],
    };
  }, [sourceNode, outputNodes, geographies]);

  // Fit bounds on load
  useEffect(() => {
    if (mapRef.current && bounds) {
      mapRef.current.fitBounds(bounds, { padding: 40, duration: 0 });
    }
  }, [bounds]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className={styles.mapFallback}>
        Set <code>VITE_MAPBOX_TOKEN</code> in <code>.env</code> to enable the
        severance map.
      </div>
    );
  }

  if (!beforeFeature || !afterFeatures || afterFeatures.length === 0) {
    return null;
  }

  const beforeCollection: FeatureCollection<Polygon> = {
    type: 'FeatureCollection',
    features: [beforeFeature],
  };

  return (
    <>
      <div className={styles.mapContainer}>
        <ReactMapbox
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={MAPBOX_STYLE}
          initialViewState={{
            longitude: bounds ? (bounds[0][0] + bounds[1][0]) / 2 : -102.1,
            latitude: bounds ? (bounds[0][1] + bounds[1][1]) / 2 : 31.97,
            zoom: 13,
          }}
          scrollZoom
          dragPan
          dragRotate={false}
          doubleClickZoom
          keyboard
          touchZoomRotate
          style={{ width: '100%', height: '100%' }}
          onLoad={() => {
            if (mapRef.current && bounds) {
              mapRef.current.fitBounds(bounds, { padding: 40, duration: 0 });
            }
          }}
        >
          {!showAfter && (
            <Source id="sev-before" type="geojson" data={beforeCollection}>
              <Layer
                id="sev-before-fill"
                type="fill"
                paint={{ 'fill-color': '#0f62fe', 'fill-opacity': 0.2 }}
              />
              <Layer
                id="sev-before-outline"
                type="line"
                paint={{
                  'line-color': '#0f62fe',
                  'line-width': 2,
                }}
              />
            </Source>
          )}

          {showAfter &&
            afterFeatures.map((feature, i) => {
              const color = ZONE_COLORS[i % ZONE_COLORS.length];
              const id = `sev-after-${i}`;
              const singleCollection: FeatureCollection<Polygon> = {
                type: 'FeatureCollection',
                features: [feature],
              };
              return (
                <Source key={id} id={id} type="geojson" data={singleCollection}>
                  <Layer
                    id={`${id}-fill`}
                    type="fill"
                    paint={{ 'fill-color': color.stroke, 'fill-opacity': 0.3 }}
                  />
                  <Layer
                    id={`${id}-line`}
                    type="line"
                    paint={{ 'line-color': color.stroke, 'line-width': 2 }}
                  />
                </Source>
              );
            })}
        </ReactMapbox>
      </div>

      <div className={styles.legend}>
        {!showAfter && (
          <div className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{
                backgroundColor: 'rgba(15, 98, 254, 0.2)',
                borderColor: '#0f62fe',
              }}
            />
            {sourceNode.property.volume.geometryName}
          </div>
        )}
        {showAfter &&
          outputNodes.map((node, i) => {
            const color = ZONE_COLORS[i % ZONE_COLORS.length];
            return (
              <div key={node.id} className={styles.legendItem}>
                <span
                  className={styles.legendSwatch}
                  style={{
                    backgroundColor: color.fill,
                    borderColor: color.stroke,
                  }}
                />
                {node.property.volume.geometryName}
              </div>
            );
          })}
      </div>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface SeveranceVisualizationProps {
  sourceNode: OwnershipNode;
  outputNodes: OwnershipNode[];
  geographies?: SeveranceGeography[];
}

export function SeveranceVisualization({
  sourceNode,
  outputNodes,
  geographies = [],
}: SeveranceVisualizationProps) {
  const [showAfter, setShowAfter] = useState(true);

  if (outputNodes.length === 0) return null;

  // Detect severance subtype from node geometry/depth differences
  const isDepthSeverance =
    outputNodes.every(
      (n) =>
        n.property.volume.geometryName ===
        sourceNode.property.volume.geometryName
    ) &&
    outputNodes.some(
      (n) =>
        n.property.volume.depthRange[0] !==
          sourceNode.property.volume.depthRange[0] ||
        n.property.volume.depthRange[1] !==
          sourceNode.property.volume.depthRange[1]
    );

  const isGeospatialSeverance =
    !isDepthSeverance &&
    outputNodes.some(
      (n) =>
        n.property.volume.geometryName !==
        sourceNode.property.volume.geometryName
    );

  if (!isDepthSeverance && !isGeospatialSeverance) return null;

  // Show Before/After toggle for all severance types
  const hasToggle = isDepthSeverance || isGeospatialSeverance;

  return (
    <div className={styles.wrapper}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionHeaderLabel}>
          Property Severance Visualization
        </span>
        {hasToggle && (
          <div className={styles.toggleGroup}>
            <button
              type="button"
              className={`${styles.toggleBtn} ${
                !showAfter ? styles.toggleBtnActive : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowAfter(false);
              }}
            >
              Before
            </button>
            <button
              type="button"
              className={`${styles.toggleBtn} ${
                showAfter ? styles.toggleBtnActive : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowAfter(true);
              }}
            >
              After
            </button>
          </div>
        )}
      </div>
      {isDepthSeverance && (
        <DepthSeverance3D
          sourceNode={sourceNode}
          outputNodes={outputNodes}
          geographies={geographies}
          showAfter={showAfter}
        />
      )}
      {isGeospatialSeverance && (
        <GeospatialSeveranceMap
          sourceNode={sourceNode}
          outputNodes={outputNodes}
          geographies={geographies}
          showAfter={showAfter}
        />
      )}
    </div>
  );
}
